import React from "react";
// COMPONENTS — the six behaviour components. Each still owns its own
// usePostCommand instance, timers, valid-state gating and cleanup; this file
// only decides which one a row gets and hands it the same props it always
// received. No command logic lives here.
import ActionButtonToggle from "../../actionButtons/ActionButtonToggle";
import ActionButtonOnRelease from "../../actionButtons/ActionButtonOnRelease";
import ActionButtonTimed from "../../actionButtons/ActionButtonTimed";
import ActionButtonEnergizeTurnback from "../../actionButtons/ActionButtonEnergizeTurnback";
import ActionButtonToggleTimer from "../../actionButtons/ActionButtonToggleTimer";
import ActionButtonSwitch from "../../actionButtons/ActionButtonSwitch";
import ActionButtonSingle from "../../actionButtons/ActionButtonSingle";
import ActionButtonOneShot from "../../actionButtons/ActionButtonOneShot";
// INTERFACES
import { BankActionInterface } from "../../../../interfaces/Bank.interface";
// CONFIG
import { ActionButtonTypeName } from "../actionTypeConfig";
import { floorSelectionCanSend } from "../dispatchProtocol";
// VERIFICATION
import { ActionVerification } from "../../actionButtons/useActionOutcome";

/**
 * Renders the command buttons for one action, dispatching on its button type.
 *
 * This replaces the six near-identical `{actionsByType[X].length > 0 && (...)}`
 * blocks that used to sit in ControlPanelLiftActions, each with its own divider,
 * icon and hand-maintained prop list. The prop lists below are unchanged from
 * those blocks — including the TURNBACK pair's shared `disableButton` lock,
 * which is deliberately group-wide and stays that way.
 *
 * The row's expected-response config is read into one object and forwarded to
 * every type: all seven behaviour components accept `ActionVerification`, and a
 * row that nominates nothing forwards nothing, which the hook reads as "report
 * Sent". Reading it once here is what keeps the eight call sites from drifting
 * on which of the two fields they pass.
 */
/**
 * The off button's label on an ON_OFF row.
 *
 * The seeder writes `toggle_label` equal to `label`, which is right for a type
 * whose two buttons send two DIFFERENT commands and wrong here: ON_OFF's
 * buttons are two edges of the SAME command, so the row rendered as two
 * adjacent buttons reading "Independent Service" and "Independent Service"
 * with nothing to say which one turns it off. Which it was could only be
 * learned by pressing one.
 *
 * An operator-chosen toggle label is always kept. Only the degenerate case —
 * empty, or a copy of the primary — falls back, and it falls back to the word
 * that names what the button does rather than to a truncation of the action's
 * name, which is what left the two indistinguishable in the first place.
 */
export function onOffToggleLabel(label: string, toggleLabel?: string): string {
  const chosen = (toggleLabel ?? "").trim();
  if (chosen !== "" && chosen !== (label ?? "").trim()) return chosen;
  return "Off";
}

export default function ActionControls({
  action,
  typeName,
  carService,
  disableTurnBack,
  setDisableTurnBack,
  targetFloor = undefined,
  registerSend = undefined,
}: {
  action: BankActionInterface;
  typeName: ActionButtonTypeName;
  carService: number;
  disableTurnBack: boolean;
  setDisableTurnBack: (newValue: boolean) => void;
  /**
   * The floor the row dispatches to, forwarded verbatim to whichever behaviour
   * component this type resolves to. `undefined` for a command that takes no
   * floor, `null` while a floor-taking one is waiting on a choice.
   */
  targetFloor?: number | null;
  /**
   * Lets the card's floor strip send this row's command directly, for a
   * one-click deployment.
   *
   * Forwarded only to the types DISPATCH_PROTOCOL permits — see the guard
   * below. A type this switch renders without it simply never registers, so
   * the strip has nothing to call.
   */
  registerSend?: (send: (floorOverride?: number) => void) => void;
}) {
  /**
   * The registration, withheld from any type whose own protocol forbids a
   * floor tap from being the sending act.
   *
   * The decision is DISPATCH_PROTOCOL's, not this switch's. That ordering is
   * the point: the firing mode governs whether a floor tap MAY send, and the
   * table governs whether this type's interaction can be collapsed at all.
   * A HOLDBACK row is not a one-click row in any configuration, because the
   * hold is the safeguard rather than a step in the way of one.
   */
  const registerSendIfPermitted = floorSelectionCanSend(typeName) ? registerSend : undefined;

  const verification: ActionVerification = {
    expectedResponse: action.expectedResponse,
    expectedResponseTimeout: action.expectedResponseTimeout,
    // What this action SENDS. A correlator verdict repeats the commanded
    // message rather than nominating a separate response, so this is what
    // identifies one — independently of whether an expectedResponse was ever
    // configured.
    commandMessageId: action.messageId,
  };

  /**
   * The paired command for a TOGGLE row, falling back to the primary when the
   * row has none.
   *
   * TOGGLE ONLY. This must not be extended to the other two-button types, and
   * an earlier version of this change did exactly that — with a justification
   * that is true of exactly one component.
   *
   * Auto-seeded Control bindings carry exactly one command. They are saved as
   * `ON_OFF` — the type meaning "one command, energised by one button and
   * de-energised by the other" — but `ON_OFF` is absent from the FlatBuffer
   * `Archive.ActionButtonType` enum, which stops at SWITCH. LiftCloud's
   * `mapActionButtonType` falls through to its default, and its own comment
   * says so: "Unknown values default to ActionButtonTypeTOGGLE". `SINGLE`
   * degrades identically.
   *
   * So every auto-seeded row arrives here as TOGGLE, and TOGGLE reads
   * `toggleMessageId`, which those rows leave empty. The off button renders
   * ENABLED, resolves to no enum, and returns before dispatching: the operator
   * presses it and nothing happens — no error, no toast, no request. Verified
   * on a real bank, where all seven seeded actions logged
   * "paired command … resolves to no enum. the id is empty".
   *
   * Falling back to the primary is correct HERE because ActionButtonToggle
   * dispatches its secondary with `active: false` — so re-using the primary
   * genuinely de-asserts it, and one command really is the whole binding.
   *
   * It is wrong everywhere else. HOLDBACK, TURNBACK, ENERGIZER_TURN_BACK,
   * TOGGLETIMER and SWITCH all dispatch their secondary with `active: TRUE`
   * (ActionButtonOnRelease:131, ActionButtonTimed:137,
   * ActionButtonEnergizeTurnback:112, ActionButtonToggleTimer:165,
   * ActionButtonSwitch:135) because their second command is a genuinely
   * different one — a turnback, a release, a cancel. Aliasing the paired slot
   * to the primary there does not synthesise an off; it asserts the same
   * command a second time and reports that as a successful "off".
   *
   * That is not theoretical: a SWITCH row with an empty toggleMessageId
   * (SWITCH is DEFAULT_ACTION_BUTTON_TYPE, so any unresolved type lands there)
   * fires the primary, then fires it AGAIN one second later from the
   * unconditional delayed step, leaving the command asserted and never
   * cleared. Before the fallback those rows failed loudly through
   * assertBoundCommand with nothing on the wire, which is the better outcome.
   * A row with a distinct paired command keeps it.
   *
   * This does not close the enum gap — an ON_OFF row is still
   * indistinguishable from a TOGGLE row on the wire, which is worth fixing in
   * the schema. It does mean the gap can no longer produce a dead button.
   */
  const toggleOnlyMessageId = action.toggleMessageId || action.messageId;
  const toggleOnlyCommand = action.toggleCommand ?? action.toggle_command ?? action.command;

  switch (typeName) {
    case "TOGGLE":
      return (
        <ActionButtonToggle
          validStates={action.validStates}
          toggleValidStates={action.toggleValidStates}
          label={action.label}
          toggleLabel={action.toggleLabel}
          messageId={action.messageId}
          toggleMessageId={toggleOnlyMessageId}
          command={action.command}
          toggleCommand={toggleOnlyCommand}
          colorAction={action.color}
          colorToggleAction={action.toggleColor}
          carService={carService}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    // Two buttons that both send the SAME command, one active and one
    // inactive. ActionButtonToggle already dispatches its primary with
    // active: true and its secondary with active: false — the only thing that
    // made it need a second command was reading the toggle fields, so ON_OFF
    // hands it the primary command in both slots. No second component, and no
    // way for the off button to end up unbound while the on button works.
    case "ON_OFF":
      return (
        <ActionButtonToggle
          validStates={action.validStates}
          toggleValidStates={action.validStates}
          label={action.label}
          toggleLabel={onOffToggleLabel(action.label, action.toggleLabel)}
          messageId={action.messageId}
          toggleMessageId={action.messageId}
          command={action.command}
          toggleCommand={action.command}
          colorAction={action.color}
          colorToggleAction={action.toggleColor}
          carService={carService}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    // One button holding both states of a single command.
    case "SINGLE":
      return (
        <ActionButtonSingle
          registerSend={registerSendIfPermitted}
          validStates={action.validStates}
          label={action.label}
          messageId={action.messageId}
          command={action.command}
          colorAction={action.color}
          carService={carService}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    // One button that pulses the command and lets the CONTROLLER clear it.
    // Single-command like SINGLE and ON_OFF, so it reads `messageId` only and
    // there is no paired slot to leave unbound.
    case "ONE_SHOT":
      return (
        <ActionButtonOneShot
          registerSend={registerSendIfPermitted}
          validStates={action.validStates}
          label={action.label}
          messageId={action.messageId}
          command={action.command}
          colorAction={action.color}
          carService={carService}
          timerAction={action.timer}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    case "HOLDBACK":
      return (
        <ActionButtonOnRelease
          validStates={action.validStates}
          label={action.label}
          toggleLabel={action.toggleLabel}
          messageId={action.messageId}
          toggleMessageId={action.toggleMessageId}
          command={action.command}
          toggleCommand={action.toggleCommand ?? action.toggle_command}
          colorAction={action.color}
          timerAction={action.timer}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    case "TURNBACK":
      return (
        <ActionButtonTimed
          validStates={action.validStates}
          label={action.label}
          toggleLabel={action.toggleLabel}
          messageId={action.messageId}
          toggleMessageId={action.toggleMessageId}
          command={action.command}
          toggleCommand={action.toggleCommand ?? action.toggle_command}
          colorAction={action.color}
          timerAction={action.timer}
          carService={carService}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
          disableButton={disableTurnBack}
          setDisableButton={setDisableTurnBack}
        />
      );

    case "ENERGIZER_TURN_BACK":
      return (
        <ActionButtonEnergizeTurnback
          validStates={action.validStates}
          label={action.label}
          toggleLabel={action.toggleLabel}
          messageId={action.messageId}
          toggleMessageId={action.toggleMessageId}
          command={action.command}
          toggleCommand={action.toggleCommand ?? action.toggle_command}
          colorAction={action.color}
          timerAction={action.timer}
          carService={carService}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    case "TOGGLETIMER":
      return (
        <ActionButtonToggleTimer
          validStates={action.validStates}
          toggleValidStates={action.toggleValidStates}
          label={action.label}
          toggleLabel={action.toggleLabel}
          messageId={action.messageId}
          toggleMessageId={action.toggleMessageId}
          command={action.command}
          toggleCommand={action.toggleCommand ?? action.toggle_command}
          colorAction={action.color}
          colorToggleAction={action.toggleColor}
          timerAction={action.timer}
          timerToggleAction={action.toggleTimer}
          carService={carService}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    case "SWITCH":
      return (
        <ActionButtonSwitch
          validStates={action.validStates}
          toggleValidStates={action.toggleValidStates}
          label={action.label}
          toggleLabel={action.toggleLabel}
          messageId={action.messageId}
          toggleMessageId={action.toggleMessageId}
          command={action.command}
          toggleCommand={action.toggleCommand ?? action.toggle_command}
          colorAction={action.color}
          timerAction={action.timer}
          carService={carService}
          targetFloor={targetFloor}
          expectedResponse={verification.expectedResponse}
          expectedResponseTimeout={verification.expectedResponseTimeout}
          commandMessageId={verification.commandMessageId}
        />
      );

    default:
      return null;
  }
}
